import React, { useState } from 'react';
import SocialSignInButtons from 'components/Auth/SocialSignInButtons';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle, MessageSquare, Phone } from 'lucide-react';
import axiosInstance from 'api/axios.config';
import { BRAND_CONFIG } from 'constants/branding';

/**
 * Creating an account without anybody from here being involved.
 *
 * <p>Until this existed the signup API had no caller: somebody arriving from an assistant, or
 * from the directory listing, reached a sign-in page for an account they did not have. This is
 * the page that makes the whole funnel reachable by a human.
 *
 * <h3>No password field, deliberately</h3>
 * The account is created inactive and finished by following an emailed link — setting the
 * password IS the email verification. So a password never travels in a signup request, and an
 * address nobody controls cannot be signed up. It also means one less field between somebody
 * and an account.
 *
 * <h3>The same answer either way</h3>
 * The API responds identically whether or not the address already has an account, because a
 * different answer turns a public endpoint into a way to enumerate customers. This page shows
 * that answer as-is rather than trying to be more helpful than the API intends to be.
 */
const Signup: React.FC = () => {
  const [businessName, setBusinessName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const BrandIcon = BRAND_CONFIG.logo.iconName === 'MessageSquare' ? MessageSquare : Phone;

  const field =
    'w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-gray-900 text-[15px] ' +
    'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 ' +
    'focus:border-transparent disabled:opacity-60 dark:bg-gray-800 dark:text-gray-100';
  const labelCls = 'block text-[13px] font-medium text-gray-700 mb-1.5';

  const complete = businessName.trim() && firstName.trim() && lastName.trim() && email.trim();

  const submit = async () => {
    if (!complete || loading) return;
    setLoading(true);
    setError('');
    try {
      await axiosInstance.post('/v1/auth/signup', {
        businessName: businessName.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
      });
      setDone(true);
    } catch (e: any) {
      const status = e?.response?.status;
      // 429 is the rate limiter and is worth saying plainly — it is the one failure the
      // person can actually do something about, by waiting.
      setError(
        status === 429
          ? 'Too many attempts. Please wait a little and try again.'
          : e?.response?.data?.message || 'We could not create the account. Please try again.',
      );
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gray-50">
      {/* ── Brand ─────────────────────────────────────────────────────────── */}
      <div className="relative lg:w-[46%] xl:w-[44%] bg-blue-950 text-white overflow-hidden
                      flex flex-col px-8 py-10 sm:px-12 lg:px-14 lg:py-14">
        <div className="relative flex items-center gap-3">
          {BRAND_CONFIG.logo.imageUrl ? (
            <img src={BRAND_CONFIG.logo.imageUrl} alt={BRAND_CONFIG.brandName} className="h-10 w-auto" />
          ) : (
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.14] ring-1 ring-white/25">
              <BrandIcon className="w-[18px] h-[18px] text-white" />
            </span>
          )}
          <div className="min-w-0">
            <p className="text-[17px] font-semibold leading-tight truncate">{BRAND_CONFIG.brandName}</p>
            {BRAND_CONFIG.brandOwner && (
              <p className="text-[12px] text-white/55 truncate">{BRAND_CONFIG.brandOwner}</p>
            )}
          </div>
        </div>

        <div className="relative hidden lg:flex flex-col justify-center flex-1 max-w-xl">
          <h1 className="text-[34px] xl:text-[40px] font-semibold leading-[1.1] tracking-[-0.02em] text-balance">
            Start in a couple of minutes
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-white/70 max-w-md">
            Create an account and set it up at your own pace. Numbers and calling need a few
            business details first — everything else you can try straight away.
          </p>
        </div>

        <p className="relative hidden lg:block text-[12px] text-white/45 mt-10">
          © {BRAND_CONFIG.footer.year} {BRAND_CONFIG.footer.companyName}. All rights reserved.
        </p>
      </div>

      {/* ── Form ──────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 sm:px-10 lg:py-14">
        <div className="w-full max-w-[380px]">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-[13px] text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>

          {done ? (
            <div className="space-y-5">
              <h2 className="text-[26px] font-semibold text-gray-900 tracking-[-0.02em]">Check your email</h2>
              <div className="flex gap-3 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800">
                <CheckCircle className="w-5 h-5 flex-none mt-0.5" />
                <div>
                  <p className="font-medium text-[14px]">Almost done</p>
                  <p className="text-[13px] mt-1 leading-relaxed">
                    If we could create an account, there is a link in your inbox to set a
                    password. The account is not usable until you follow it.
                  </p>
                </div>
              </div>
              <Link to="/login" className="tl-btn tl-btn-primary w-full py-3 text-[15px] block text-center">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-[26px] font-semibold text-gray-900 tracking-[-0.02em]">
                Create your account
              </h2>
              <p className="mt-2 text-[14px] leading-relaxed text-gray-600">
                No password needed here — we will email you a link to set one.
              </p>

              <form className="mt-7 space-y-5" onSubmit={(e) => { e.preventDefault(); submit(); }}>
                <div>
                  <label className={labelCls} htmlFor="businessName">Business name</label>
                  <input
                    id="businessName" type="text" autoComplete="organization"
                    value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                    className={field} placeholder="Acme Wellness" disabled={loading} maxLength={120}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls} htmlFor="firstName">First name</label>
                    <input
                      id="firstName" type="text" autoComplete="given-name"
                      value={firstName} onChange={(e) => setFirstName(e.target.value)}
                      className={field} placeholder="Priya" disabled={loading} maxLength={60}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lastName">Last name</label>
                    <input
                      id="lastName" type="text" autoComplete="family-name"
                      value={lastName} onChange={(e) => setLastName(e.target.value)}
                      className={field} placeholder="Sharma" disabled={loading} maxLength={60}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelCls} htmlFor="email">Work email</label>
                  <input
                    id="email" type="email" autoComplete="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className={field} placeholder="you@company.com" disabled={loading} maxLength={160}
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700">
                    <AlertCircle className="w-4 h-4 flex-none" />
                    <span className="text-[13px]">{error}</span>
                  </div>
                )}

                <button
                  type="submit" disabled={loading || !complete}
                  className="tl-btn tl-btn-primary w-full py-3 text-[15px] disabled:opacity-60"
                >
                  {loading ? 'Creating…' : 'Create account'}
                </button>

                <p className="text-[12px] leading-relaxed text-gray-500">
                  By creating an account you agree to our{' '}
                  <a href={BRAND_CONFIG.terms.url} target="_blank" rel="noopener noreferrer"
                     className="text-blue-700 hover:underline">
                    {BRAND_CONFIG.terms.linkText}
                  </a>{' '}
                  and{' '}
                  <a href={BRAND_CONFIG.terms.privacyUrl} target="_blank" rel="noopener noreferrer"
                     className="text-blue-700 hover:underline">
                    {BRAND_CONFIG.terms.privacyText}
                  </a>.
                </p>
              </form>

              {/* One click instead of a form, and the provider has already verified
                  the address — so a social signup skips the emailed confirmation the
                  password flow needs. */}
              <SocialSignInButtons intent="signup" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Signup;
